import { type BoundaryEditAction } from '~/boundary/editMode/boundaryEditModal/boundaryEditModal.helpers';
import { type MapShapeInstance } from '~/map/map/mapObjects/mapShape/mapShapeModel';
import {
  type BoundaryGroupId, type BoundaryId, type BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';

export enum DrawType {
  Draw = 'Draw',
  Drag = 'Drag',
}

export enum BoundaryDrawMode {
  BoundaryPolygonEdit = 'BoundaryPolygonEdit',
  BoundaryAdvancedEdit = 'BoundaryAdvancedEdit',
}

type BoundaryPolygonEditModeData = Readonly<{
  mode: BoundaryDrawMode.BoundaryPolygonEdit;
  boundaryId: BoundaryId;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
}>;

type BoundaryAdvancedEditModeData = Readonly<{
  mode: BoundaryDrawMode.BoundaryAdvancedEdit;
  action: BoundaryEditAction;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  boundaryId?: BoundaryId;
}>;

export type BoundaryDrawInactiveState = Readonly<{
  isModalOpen: false;
  isActive: false;
  boundaryGroupId: null;
  drawType: null;
  mapShapes: null;
  initialShapes: null;
}>;

type BoundaryDrawActiveStateBase = Readonly<{
  isModalOpen: true;
  isActive: boolean;
  boundaryGroupId: BoundaryGroupId;
  drawType: DrawType;
  mapShapes: readonly MapShapeInstance[];
  initialShapes: readonly MapShapeInstance[];
}>;

export type BoundaryPolygonEditActiveState = BoundaryDrawActiveStateBase & Readonly<{
  drawModeData: BoundaryPolygonEditModeData;
  disabledReason?: string;
}>;

export type BoundaryAdvancedEditActiveState = BoundaryDrawActiveStateBase & Readonly<{
  drawModeData: BoundaryAdvancedEditModeData;
}>;

export type BoundaryDrawState = BoundaryDrawInactiveState | BoundaryPolygonEditActiveState | BoundaryAdvancedEditActiveState;

export const isBoundaryDrawActive = (state: BoundaryDrawState): state is (BoundaryPolygonEditActiveState | BoundaryAdvancedEditActiveState) => state.isActive;

export const isBoundaryPolygonEditActive = (state: BoundaryDrawState): state is BoundaryPolygonEditActiveState =>
  state.isActive && state.drawModeData.mode === BoundaryDrawMode.BoundaryPolygonEdit;

export const isBoundaryPolygonEditModalOpen = (state: BoundaryDrawState): state is BoundaryPolygonEditActiveState =>
  state.isModalOpen && state.drawModeData.mode === BoundaryDrawMode.BoundaryPolygonEdit;

export const isBoundaryAdvancedEditActive = (state: BoundaryDrawState): state is BoundaryAdvancedEditActiveState =>
  state.isActive && state.drawModeData.mode === BoundaryDrawMode.BoundaryAdvancedEdit;

export const isBoundaryAdvancedEditModalOpen = (state: BoundaryDrawState): state is BoundaryAdvancedEditActiveState =>
  state.isModalOpen && state.drawModeData.mode === BoundaryDrawMode.BoundaryAdvancedEdit;
